import React, { useEffect, useRef } from 'react'
import { Dialog } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ModalDialog } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'

import { Form } from '@bpmn-io/form-js'
import '@bpmn-io/form-js/dist/assets/form-js.css'
import FileUploadModule from '../extensions/form/custom-file-render'
import FileDownloadModule from '../extensions/form/custom-file-download'
import CustomSelectModule from '../extensions/form/custom-http-bound-connector'
import DocumentScannerModule from '../extensions/form/custom-document-scanner'

type FormPreviewModalProps = {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  schema: any
  data?: any
  readOnly?: boolean
}

const FormPreviewModal: React.FC<FormPreviewModalProps> = ({ open, setOpen, schema, data, readOnly }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const form = useRef<Form | null>(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return // Don't do anything unless modal is open

    const timeout = setTimeout(() => {
      if (containerRef.current && schema) {
        form.current = new Form({
          container: containerRef.current,
          additionalModules: [FileUploadModule, FileDownloadModule, CustomSelectModule, DocumentScannerModule],
        })

        form.current
          .importSchema(schema, data)
          .then(() => form.current?.setProperty('readOnly', readOnly))
          .catch((err) => console.error('Failed to import form', err))
      }
    }, 50) // small delay

    return () => {
      clearTimeout(timeout)
      form.current?.destroy()
      form.current = null
    }
  }, [open, schema, data, readOnly])

  return (
    <ModalDialog isOpen={open} setOpen={setOpen} className='!max-w-xl'>
      <Dialog.Title as='h3' className='leading-6 text-lg flex justify-between'>
        {t('preview')}
        <button onClick={() => setOpen(false)}>
          <XMarkIcon className='w-5 h-5 stroke-gray-500' />
        </button>
      </Dialog.Title>

      <div ref={containerRef} />
    </ModalDialog>
  )
}

export default FormPreviewModal
